import type { LegalDocument } from '@/schemas/schema';
import { parseJsonResponse } from './ai-utils';
import { queryBlocksAi } from '@/lib/blocks-ai';
import {
  DEFAULT_DOCUMENT_SYSTEM_PROMPT,
  buildDocumentUserPrompt,
  finalizeLegalDocument,
  type GenerateDocumentOptions,
  type GenerateDocumentResult,
} from './generate-document-shared';
import { generateDocumentViaAgent } from './generate-document-via-agent';

export type { GenerateDocumentOptions, GenerateDocumentResult };

// ---------------------------------------------------------------------------
// Engine selector — flip via env var. The new agent-backed path is the
// quality target (6–12 page output); this legacy direct-AI path stays as
// an instant rollback.
// ---------------------------------------------------------------------------

const USE_AGENT = import.meta.env.VITE_GENERATE_DOCUMENT_USE_AGENT === 'true';

// ---------------------------------------------------------------------------
// Legacy direct-AI prompt builder (kept here, not in the shared module,
// because only this engine uses it — the agent owns its own system prompt
// server-side).
// ---------------------------------------------------------------------------

const JSON_OUTPUT_INSTRUCTIONS = `

## CRITICAL: JSON OUTPUT REQUIREMENT

You MUST return ONLY a valid JSON object. Do NOT return markdown, plain text, or any text outside the JSON structure. No backticks, no code blocks.

Return a JSON object matching this EXACT structure:

{
  "metadata": {
    "title": "Document Title",
    "effectiveDate": "YYYY-MM-DD",
    "documentType": "document-type",
    "jurisdiction": "State/Country",
    "generatedAt": "ISO 8601 timestamp"
  },
  "content": [
    {
      "type": "article",
      "props": { "title": "ARTICLE TITLE", "number": "1" },
      "children": [
        {
          "type": "section",
          "props": { "title": "Optional Section Title", "number": "1.1" },
          "children": [
            { "type": "paragraph", "text": "Content here..." }
          ]
        }
      ]
    }
  ],
  "signatories": [
    {
      "party": "partyType",
      "name": "Full Name",
      "email": "email@example.com",
      "title": "Title/Role"
      "address": "Mailing Address",
      "phone": "Phone Number",
      "company": "Company Name"
    }
  ]
}

## BLOCK TYPES: article > section > (paragraph | list > list_item | definition > definition_item)
Always produce complete, thorough, and unabridged documents.
Never truncate, summarize, or shorten content.
Err on the side of more detail, not less."

Do NOT include signature blocks or "In Witness Whereof" clauses in the content array.
Use EXACT names and details from the user prompt — never use placeholders.`;

function buildDocumentSystemPrompt(options: {
  templateTitle: string;
  systemPrompt?: string;
  systemPromptRole?: string;
}): string {
  const parts: string[] = [];

  if (options.systemPromptRole) {
    parts.push(`You are ${options.systemPromptRole}.`);
  } else {
    parts.push(
      `You are an expert legal drafter specializing in ${options.templateTitle} documents.`
    );
  }

  if (options.systemPrompt) {
    parts.push(options.systemPrompt);
  } else {
    parts.push(DEFAULT_DOCUMENT_SYSTEM_PROMPT);
  }

  parts.push(JSON_OUTPUT_INSTRUCTIONS);

  return parts.join('\n\n');
}

// ---------------------------------------------------------------------------
// Main export — dispatches to the agent engine when the flag is on,
// otherwise runs the legacy direct-AI path below.
// ---------------------------------------------------------------------------

export async function generateDocument(
  options: GenerateDocumentOptions
): Promise<GenerateDocumentResult> {
  if (USE_AGENT) {
    return generateDocumentViaAgent(options);
  }

  const { templateTitle, templateSlug, systemPrompt, systemPromptRole, formData, signal } = options;

  const fullSystemPrompt = buildDocumentSystemPrompt({
    templateTitle,
    systemPrompt,
    systemPromptRole,
  });
  const userPrompt = buildDocumentUserPrompt(formData, templateTitle);

  const response = await queryBlocksAi({
    query: userPrompt,
    basePrompt: fullSystemPrompt,
    signal,
  });

  if (!response.message) {
    throw new Error('AI returned an empty response');
  }

  let parsed: Partial<LegalDocument>;
  try {
    parsed = parseJsonResponse(response.message) as Partial<LegalDocument>;
  } catch (e) {
    console.error('[generateDocument] FULL RAW RESPONSE:', response.message);
    throw e;
  }

  const document = finalizeLegalDocument(parsed, {
    templateTitle,
    templateSlug,
    formData,
  });

  return { document, formData };
}
